import { KatBadge, KatToggle } from "@amzn/katal-react";
import classnames from "classnames";
import React, { type FC, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import type { DynamicTableData } from "src/api/interfaces/inbound_compliance_group";
import type { PageInformation } from "src/api/interfaces/pagination";
import DataTable from "src/components/DataTable";
import convertToDynamicTableDefinition from "src/components/Pages/DocumentInbound/Tabs/InboundCompliance/InboundComplianceModals/Listing/dynamic-data-table-definition";
import convertToDynamicTableFields from "src/components/Pages/DocumentInbound/Tabs/InboundCompliance/InboundComplianceModals/Listing/dynamic-table-fields-extractor";
import { useInboundComplianceGroupDetails } from "src/components/Pages/DocumentInbound/Tabs/InboundCompliance/InboundComplianceModals/Listing/InboundComplianceGroupDetailsContext";
import PhysicalReceiptTableRow from "src/components/Pages/DocumentInbound/Tabs/InboundCompliance/InboundComplianceModals/Listing/PhysicalReceiptTableRow";
import usePageInformation from "src/components/Pagination/hooks/usePageInformation";
import PaginationInMemory from "src/components/Pagination/PaginationInMemory";
import {
  DEFAULT_TRANSLATION_CONFIG,
  DEFAULT_TRANSLATION_NS,
} from "src/components/TranslatedField/TranslationKey";
import { InboundComplianceGroupDetailsField as GroupDetails } from "src/constants";
import publishMetric, { MetricAction } from "src/metrics";
import styles from "src/styles.module.scss";

export const sortByAscendingItems = (rowData: Array<any>): Array<any> => {
  return rowData.sort((firstField, secondField) =>
    firstField[GroupDetails.ITEM_ID.valueOf()] <
    secondField[GroupDetails.ITEM_ID.valueOf()]
      ? -1
      : 1
  );
};

export const filterWithDiscrepancies = (rowData: Array<any>) => {
  publishMetric(MetricAction.INBOUND_COMPLIANCE_FILTER_WITH_DISCREPANCIES);
  return rowData.filter(
    (item) =>
      item[GroupDetails.RECEIVED.valueOf()] !==
      item[GroupDetails.EXPECTED.valueOf()]
  );
};

export const DynamicTable: FC = () => {
  const { t } = useTranslation(
    DEFAULT_TRANSLATION_NS,
    DEFAULT_TRANSLATION_CONFIG
  );
  const { inboundComplianceGroupDetails, groupDetailsRetrievalState } =
    useInboundComplianceGroupDetails();
  const dynamicDataTableDefinition = convertToDynamicTableDefinition(
    inboundComplianceGroupDetails
  );
  const [dynamicDataTableFields, setDynamicDataTableFields] =
    useState<DynamicTableData>({
      rowData: sortByAscendingItems(
        inboundComplianceGroupDetails.map((entry) =>
          convertToDynamicTableFields(entry, dynamicDataTableDefinition)
        )
      ),
    });
  const [initialRowData] = useState(dynamicDataTableFields.rowData);
  const [discrepantItems] = useState(
    filterWithDiscrepancies(dynamicDataTableFields.rowData)
  );
  const [currentItems, setCurrentItems] = useState(initialRowData.length);
  const { pageInformation, setPageInformation } = usePageInformation({
    updateUrl: false,
  });
  const [tablePage, setTablePage] = useState<Array<any>>();
  const [filterDiscrepancies, setFilterDiscrepancies] =
    useState<boolean>(false);

  const onChangePage = useCallback(
    (items: Array<any>, newPageInformation: PageInformation) => {
      setTablePage(items);
      setPageInformation(newPageInformation);
    },
    [setPageInformation, setTablePage]
  );

  const onChangeFilter = useCallback(() => {
    setFilterDiscrepancies(!filterDiscrepancies);
    const showedData = !filterDiscrepancies ? discrepantItems : initialRowData;
    setCurrentItems(showedData.length);
    setDynamicDataTableFields({ rowData: showedData });
    const newPageInformation: PageInformation = {
      page: 1,
      pageSize: pageInformation.pageSize,
      total: pageInformation.total,
    };
    onChangePage(
      showedData.slice(0, pageInformation.pageSize),
      newPageInformation
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterDiscrepancies]);

  return (
    <div>
      <div className={classnames(styles.row_flex_non_wrap_space_between)}>
        <KatToggle
          textUnchecked={t("aic_doc_page_inbound_compliance_toggle_filter_off")}
          textChecked={t("aic_doc_page_inbound_compliance_toggle_filter_on")}
          checked={filterDiscrepancies}
          onChange={onChangeFilter}
        />
        <KatBadge
          label={t("aic_doc_page_inbound_compliance_showing_items_badge", {
            currentItems: currentItems,
            totalItems: initialRowData.length,
          })}
        />
      </div>

      <DataTable
        id="dynamic-table"
        contents={tablePage}
        tableDefinition={dynamicDataTableDefinition}
        retrievalState={groupDetailsRetrievalState}
        CustomDataTableRow={PhysicalReceiptTableRow}
      />
      <PaginationInMemory
        page={pageInformation.page}
        pageSize={pageInformation.pageSize}
        items={dynamicDataTableFields.rowData}
        onChange={onChangePage}
        updateUrl={false}
      />
    </div>
  );
};

export default DynamicTable;
